/* Страница программы (TPL-2) — как в прототипе (program.html): программа — цикл дней (тренировка или отдых),
   который повторяется, пока не выйдет срок. Своя правится здесь и сохраняется сама; общая из базы сервиса только
   смотрится — «Сохранить как своё» делает копию. Новая (/programs/new) появляется в базе с первой правкой.
   Главное действие — «Назначить»: тренировки программы встают выбранным клиентам и группам с нужной даты обычными
   днями календаря. За клиентом программа не закрепляется — это лишь способ поставить много тренировок разом. */
import { trpc } from '@tg/app-core';
import { blockCount, cloneContent, dayHas, type WorkoutContent } from '@tg/domain';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DatePicker, type Who } from '../calendar/Calendar';
import { ICON as CICON } from '../constructor/icons';
import { Svg } from '../constructor/Svg';
import { useTemplates, type Template } from '../lib/data';
import { addDays, dlong, humanDate, plural, today } from '../lib/format';
import { usePageTitle } from '../shell/Shell';
import { Cav } from '../ui/Cav';
import { ClientPicker, useWhoList } from '../ui/ClientPicker';
import { ICON } from '../ui/icons';
import { Modal } from '../ui/Modal';
import { toast } from '../ui/Toast';

export type Seq = (WorkoutContent | null)[];
export const progOf = (t: Template | undefined) => {
  const c = (t?.content || {}) as { seq?: Seq; days?: number };
  const seq = c.seq || [];
  return { seq, days: c.days || seq.length || 28 };
};
const N = (n: number, a: string, b: string, c: string) => n + ' ' + plural(n, a, b, c);
const woName = (w: WorkoutContent) => w.title || 'Тренировка без названия';

export function ProgramPage() {
  const { id = 'new' } = useParams();
  const isNew = id === 'new';
  const nav = useNavigate();
  const tq = useTemplates('program');
  const wq = useTemplates('workout');
  const utils = trpc.useUtils();
  const P = isNew ? undefined : tq.data?.find((t) => t.id === id);
  usePageTitle(isNew ? 'Новая программа' : 'Программа');
  const own = isNew || !!P?.trainerId;
  const { seq, days } = progOf(P);
  const [title, setTitle] = useState('');
  const [goal, setGoal] = useState('');
  const [len, setLen] = useState('');
  useEffect(() => {
    setTitle(P?.title || '');
    setGoal(P?.goal || '');
    setLen(String(days));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [P?.id, P?.updatedAt]);
  const [assign, setAssign] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [drag, setDrag] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  const saveM = trpc.trainer.saveTemplate.useMutation({ onError: (e) => toast(e.message || 'Не сохранилось') });
  const delM = trpc.trainer.deleteTemplate.useMutation();

  /* Правка своей: сразу в базу; новая — появляется с первой правкой */
  const save = (patch: { title?: string; goal?: string; seq?: Seq; days?: number }, msg?: string | false) => {
    const t = (patch.title ?? P?.title ?? title).trim() || 'Новая программа';
    const content = { seq: patch.seq ?? seq, days: patch.days ?? days };
    saveM.mutate(
      { id: P?.id, level: 'program', title: t, goal: (patch.goal ?? P?.goal ?? goal) || null, content },
      {
        onSuccess: (x) => {
          utils.trainer.templates.invalidate({ level: 'program' });
          if (!P) {
            toast('Программа добавлена в вашу базу');
            nav('/programs/' + x.id, { replace: true });
          } else if (msg !== false) toast(msg || 'Сохранено');
        },
      },
    );
  };
  const copy = (t: string) =>
    saveM.mutate(
      { level: 'program', title: t, goal: P?.goal || null, content: cloneContent({ seq, days }) },
      {
        onSuccess: (x) => {
          utils.trainer.templates.invalidate({ level: 'program' });
          toast('Копия в вашей базе — её и правьте');
          nav('/programs/' + x.id);
        },
      },
    );

  if (!isNew && tq.data && !P)
    return (
      <div className="cons one">
        <main>
          <section className="pane">
            <div className="empty">
              <div className="t">Программы нет в базе</div>
              <div className="d">
                <Link to="/programs">Вернуться к программам</Link>
              </div>
            </div>
          </section>
        </main>
      </div>
    );
  if (!isNew && !P) return null;

  const wos = wq.data || [];
  const nw = seq.filter(Boolean).length;
  const exN = seq.reduce((n, w) => n + (w ? w.blocks.reduce((a, b) => a + blockCount(b), 0) : 0), 0);
  const setDay = (k: number, v: string) => {
    const next = [...seq];
    if (!v) next[k] = null;
    else if (v !== '__cur') {
      const t = wos.find((x) => x.id === v);
      if (t) next[k] = { ...cloneContent(t.content as WorkoutContent), title: t.title };
    }
    save({ seq: next }, false);
  };
  const row = (w: WorkoutContent | null, k: number) => {
    const meta = w ? `${N(w.blocks.length, 'блок', 'блока', 'блоков')} · ${w.blocks.reduce((a, b) => a + blockCount(b), 0)} упр.` : '';
    if (!own)
      return (
        <div key={k} className={'cyc ro' + (w ? '' : ' rest')}>
          <span className="cn">День {k + 1}</span>
          <span className="cw">{w ? woName(w) : 'Отдых'}</span>
          <span className="cmeta">{meta}</span>
        </div>
      );
    const tid = w ? wos.find((t) => t.title === w.title)?.id : '';
    return (
      <div
        key={k}
        className={'cyc' + (w ? '' : ' rest') + (drag === k ? ' drag' : '') + (over === k && drag !== k ? ' over' : '')}
        draggable
        onDragStart={(e) => {
          setDrag(k);
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', String(k));
        }}
        onDragEnd={() => (setDrag(null), setOver(null))}
        onDragOver={(e) => {
          if (drag == null) return;
          e.preventDefault();
          setOver(k);
        }}
        onDrop={(e) => {
          e.preventDefault();
          if (drag == null || drag === k) return;
          const next = [...seq];
          const [m] = next.splice(drag, 1);
          next.splice(k, 0, m);
          setDrag(null);
          setOver(null);
          save({ seq: next }, false);
        }}
      >
        <span className="gr" title="Перетащить день">
          <Svg src={CICON.grip} />
        </span>
        <span className="cn">День {k + 1}</span>
        <select className="inp" value={w ? tid || '__cur' : ''} onChange={(e) => setDay(k, e.target.value)}>
          <option value="">Отдых</option>
          {w && !tid && <option value="__cur">{woName(w)}</option>}
          {wos.map((t) => (
            <option key={t.id} value={t.id} data-sub={t.trainerId ? undefined : 'общая база'}>
              {t.title}
            </option>
          ))}
        </select>
        <span className="cmeta">{meta}</span>
        <span className="lnk"></span>
        <button className="iact" title="Убрать день из цикла" onClick={() => save({ seq: seq.filter((_, j) => j !== k) }, false)}>
          ✕
        </button>
      </div>
    );
  };
  const firstWo = wos[0];

  return (
    <div className="cons one">
      <main>
        <section className="pane">
          <div className="ehead">
            <nav className="crumb">
              <Link to="/programs">Программы</Link>
              <span className="sep">/</span>
              <span className="cur">{title || 'Новая программа'}</span>
            </nav>
            <div className="ehead-top">
              <span className="thumb eth">
                <ICON.prog />
              </span>
              <div className="eid">
                {own ? (
                  <input
                    className="enm"
                    value={title}
                    placeholder="Название программы"
                    aria-label="Название программы"
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={() => title.trim() && title.trim() !== (P?.title || '') && save({ title: title.trim() })}
                    onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                  />
                ) : (
                  <div className="enm">{P!.title}</div>
                )}
                <div className="esub">
                  {own ? <span className="chip ok">своё</span> : <span className="chip ghost">общая база</span>}
                  <span>{N(days, 'день', 'дня', 'дней')}</span>
                  <span>цикл {N(seq.length, 'день', 'дня', 'дней')}</span>
                  <span>{N(nw, 'тренировка', 'тренировки', 'тренировок')} в цикле</span>
                </div>
              </div>
              <div className="eacts">
                {P && (
                  <button className="btn" disabled={!nw} title={nw ? undefined : 'В цикле нет тренировок'} onClick={() => setAssign(true)}>
                    <ICON.users /> Назначить
                  </button>
                )}
                {P && own && (
                  <>
                    <button className="btn gh" onClick={() => copy(P.title + ' — копия')}>
                      Дублировать
                    </button>
                    <button className="btn gh" onClick={() => setConfirmDel(true)}>
                      Удалить
                    </button>
                  </>
                )}
                {P && !own && (
                  <button className="btn gh" onClick={() => copy(P.title)}>
                    Сохранить как своё
                  </button>
                )}
              </div>
            </div>
          </div>
          {!own && (
            <div className="ebar">
              <span>
                <b>Программа из общей базы</b>
                <s>Назначить можно как есть. Чтобы поменять — «Сохранить как своё»: появится ваша копия, её и правьте.</s>
              </span>
            </div>
          )}
          {isNew && (
            <div className="ebar">
              <span>
                <b>Новая программа</b>
                <s>Появится в вашей базе с первой правкой. Всё сохраняется само.</s>
              </span>
            </div>
          )}

          <div className="egrid">
            <div>
              <div className="sechead">
                <span className="t">Основное</span>
                <span className="ln"></span>
              </div>
              <div className="card cb ecard">
                {own ? (
                  <>
                    <div className="formfld" style={{ marginBottom: 12 }}>
                      <span className="k">Цель</span>
                      <textarea className="inp" rows={2} placeholder="Чего добиваемся за программу" value={goal} onChange={(e) => setGoal(e.target.value)} onBlur={() => goal.trim() !== (P?.goal || '') && save({ goal: goal.trim() })} />
                    </div>
                    <div className="formfld" style={{ maxWidth: 220 }}>
                      <span className="k">Длительность, дней</span>
                      <input
                        className="inp"
                        type="number"
                        min={1}
                        max={365}
                        value={len}
                        onChange={(e) => setLen(e.target.value)}
                        onBlur={() => {
                          const v = Math.max(1, Math.min(365, parseInt(len, 10) || days));
                          setLen(String(v));
                          if (v !== days || !P) save({ days: v });
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="kv" style={{ marginTop: 0 }}>
                    <s>Цель</s>
                    <b>{P!.goal || '—'}</b>
                    <s>Длительность</s>
                    <b>{N(days, 'день', 'дня', 'дней')}</b>
                  </div>
                )}
              </div>

              <div className="sechead">
                <span className="t">Цикл</span>
                <span className="ln"></span>
                <span className="n">{seq.length ? `повторяется до ${days}-го дня` : ''}</span>
              </div>
              <div className="card cb ecard">
                {seq.length ? (
                  <div className="cycs">{seq.map(row)}</div>
                ) : (
                  <div className="hint" style={{ marginTop: 0 }}>
                    В цикле пока нет дней. Добавьте тренировку или день отдыха.
                  </div>
                )}
                {own && (
                  <div className="cycadd">
                    <button
                      className="btn gh sm"
                      disabled={!firstWo}
                      title={firstWo ? undefined : 'Сначала сохраните тренировку в базу — из конструктора'}
                      onClick={() => save({ seq: [...seq, firstWo ? { ...cloneContent(firstWo.content as WorkoutContent), title: firstWo.title } : null] }, false)}
                    >
                      <ICON.plus /> Тренировка
                    </button>
                    <button className="btn gh sm" onClick={() => save({ seq: [...seq, null] }, false)}>
                      <ICON.plus /> Отдых
                    </button>
                  </div>
                )}
                <div className="hint">Цикл — дни подряд: тренировка из базы или отдых. Кончился цикл — начинается заново, пока не выйдет срок программы.</div>
              </div>
            </div>

            <div className="side">
              <div className="card cb ecard">
                <div className="kv" style={{ marginTop: 0 }}>
                  <s>Источник</s>
                  <b>{own ? 'ваша база' : 'общая база сервиса'}</b>
                  {P && (
                    <>
                      <s>Назначена</s>
                      <b>{P.used ? P.used + '×' : '—'}</b>
                    </>
                  )}
                  {P?.createdAt && (
                    <>
                      <s>Создана</s>
                      <b>{dlong(new Date(P.createdAt as unknown as string).toISOString().slice(0, 10))}</b>
                    </>
                  )}
                  <s>Тренировок в цикле</s>
                  <b>{nw}</b>
                  <s>Упражнений за цикл</s>
                  <b>{exN}</b>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      {assign && P && <AssignProgram tpl={P} onClose={() => setAssign(false)} />}
      {confirmDel && P && (
        <Modal
          title="Удалить программу"
          onClose={() => setConfirmDel(false)}
          foot={
            <>
              <span className="sp"></span>
              <button className="btn gh" onClick={() => setConfirmDel(false)}>
                Отмена
              </button>
              <button
                className="btn rm"
                onClick={() =>
                  delM.mutate(
                    { id: P.id },
                    {
                      onSuccess: () => {
                        utils.trainer.templates.invalidate({ level: 'program' });
                        toast('Программа удалена');
                        nav('/programs', { replace: true });
                      },
                    },
                  )
                }
              >
                Удалить
              </button>
            </>
          }
        >
          <p className="lead">«{P.title}» уйдёт из вашей базы. Клиентам, которым она уже стоит в календаре, ничего не изменится.</p>
        </Modal>
      )}
    </div>
  );
}

/* ─── «Назначить программу»: кому, с какой даты, как ставить. Разметка — как у окна копирования (calendar/Bulk) ─── */
const whoId = (w: Who) => ('clientId' in w ? w.clientId : w.groupId);
const toWho = (w: { kind: 'client' | 'group'; id: string }): Who => (w.kind === 'group' ? { groupId: w.id } : { clientId: w.id });

export function AssignProgram({ tpl, onClose, initial }: { tpl: Template; onClose: () => void; initial?: Who[] }) {
  const W = useWhoList();
  const utils = trpc.useUtils();
  const [to, setTo] = useState<Who[]>(initial || []);
  const [from, setFrom] = useState(today());
  const [publish, setPublish] = useState(true);
  const [replace, setReplace] = useState(false);
  const [addEl, setAddEl] = useState<HTMLElement | null>(null);
  const [dateEl, setDateEl] = useState<HTMLElement | null>(null);
  const dry = trpc.trainer.applyProgram.useMutation();
  const apply = trpc.trainer.applyProgram.useMutation();
  const undoM = trpc.trainer.bulkUndo.useMutation();
  const { seq, days } = progOf(tpl);
  const nw = seq.filter((w) => w && dayHas(w)).length;
  const key = JSON.stringify([to, from, replace]);
  useEffect(() => {
    if (to.length) dry.mutate({ id: tpl.id, to, from, publish, replace, dry: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const pv = to.length ? dry.data : null;
  const av = (id: string) => {
    const w = W.byId(id);
    return <Cav ini={w?.ini} photo={w?.kind === 'client' ? w.photo : null} grp={w?.kind === 'group'} />;
  };
  const go = () =>
    apply.mutate(
      { id: tpl.id, to, from, publish, replace },
      {
        onSuccess: async (r) => {
          await Promise.all([utils.trainer.days.invalidate(), utils.trainer.clients.invalidate(), utils.trainer.templates.invalidate({ level: 'program' }), utils.trainer.groupsTill.invalidate()]);
          onClose();
          const who = to.length > 1 ? N(to.length, 'клиенту', 'клиентам', 'клиентам') : W.byId(whoId(to[0]))?.name || '';
          toast(`«${tpl.title}» назначена · ${who} · ${N(r.placed, 'тренировка', 'тренировки', 'тренировок')}${publish ? '' : ' черновиками'}`, 'Отменить', async () => {
            await undoM.mutateAsync({ undo: r.undo });
            await utils.trainer.days.invalidate();
            utils.trainer.clients.invalidate();
            toast('Назначение отменено');
          });
        },
        onError: (e) => toast(e.message || 'Не получилось'),
      },
    );
  const till = addDays(from, days - 1);
  return createPortal(
    <div className="ov on bk-dlg" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="md bkmd">
        <div className="mdh">
          <span className="dot"></span>
          <h2>Назначить «{tpl.title}»</h2>
          <button className="cls" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="mdb bkd">
          <section className="bkd-step">
            <span className="bkd-num">1</span>
            <div className="bkd-body">
              <div className="bkd-t">Кому</div>
              <div className="bkd-to">
                {to.map((w) => (
                  <span className="bkd-chip" key={whoId(w)}>
                    {av(whoId(w))}
                    <b>{W.byId(whoId(w))?.name}</b>
                    <button aria-label="Убрать" onClick={() => setTo(to.filter((x) => whoId(x) !== whoId(w)))}>
                      <Svg src={ICONS.x} />
                    </button>
                  </span>
                ))}
                <button className="bkd-add" onClick={(e) => setAddEl(e.currentTarget)}>
                  <Svg src={ICONS.plus} />
                  {to.length ? 'Ещё клиент или группа' : 'Выбрать клиента или группу'}
                </button>
              </div>
            </div>
          </section>
          <section className="bkd-step">
            <span className="bkd-num">2</span>
            <div className="bkd-body">
              <div className="bkd-t">С какой даты</div>
              <div className="bkd-date">
                <button className="btn gh sm jumpb" onClick={(e) => setDateEl(e.currentTarget)}>
                  <Svg src={ICONS.cal} />
                  {humanDate(from)}
                  <Svg src={ICONS.chev} />
                </button>
                <span className="mut" style={{ fontSize: 14 }}>
                  {N(days, 'день', 'дня', 'дней')}, до {humanDate(till)} · {N(nw, 'тренировка', 'тренировки', 'тренировок')} в цикле из {seq.length}
                </span>
              </div>
            </div>
          </section>
          <section className="bkd-step">
            <span className="bkd-num">3</span>
            <div className="bkd-body">
              <div className="bkd-t">Как ставить</div>
              <div className="bkd-rules">
                <button className={publish ? 'on' : ''} onClick={() => setPublish(true)}>
                  <b>Опубликовать</b>
                  <s>Клиент сразу видит тренировки</s>
                </button>
                <button className={!publish ? 'on' : ''} onClick={() => setPublish(false)}>
                  <b>Черновиками</b>
                  <s>Проверите и опубликуете сами</s>
                </button>
              </div>
              {pv && pv.busy > 0 && (
                <div className="bkd-rules" style={{ marginTop: 8 }}>
                  <button className={!replace ? 'on' : ''} onClick={() => setReplace(false)}>
                    <b>Занятые дни не трогать</b>
                    <s>Уже составленное останется</s>
                  </button>
                  <button className={replace ? 'on' : ''} onClick={() => setReplace(true)}>
                    <b>Заменить занятые</b>
                    <s>Тренировки программы встанут поверх</s>
                  </button>
                </div>
              )}
            </div>
          </section>
        </div>
        <div className="mdf">
          <span className="bkd-sum">
            {!to.length
              ? 'Выберите, кому назначить'
              : pv
                ? `Встанет: ${pv.placed}` + (pv.busy ? ` · занято дней: ${pv.busy}${replace ? ' — заменятся' : ' — пропустим'}` : '')
                : 'Считаем…'}
          </span>
          <span className="sp"></span>
          <button className="btn gh" onClick={onClose}>
            Отмена
          </button>
          <button className="btn" disabled={!to.length || apply.isPending || (!!pv && !pv.placed)} onClick={go}>
            {apply.isPending ? 'Назначаем…' : 'Назначить'}
          </button>
        </div>
      </div>
      {addEl && (
        <ClientPicker
          anchor={addEl}
          opts={{ exclude: new Set(to.map(whoId)) }}
          onPick={(w) => {
            setAddEl(null);
            setTo([...to, toWho(w)]);
          }}
          onClose={() => setAddEl(null)}
        />
      )}
      {dateEl && (
        <DatePicker
          anchor={dateEl}
          value={from}
          onPick={(d) => {
            setDateEl(null);
            setFrom(d);
          }}
          onClose={() => setDateEl(null)}
        />
      )}
    </div>,
    document.body,
  );
}

const ICONS = {
  x: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M4 4l8 8M12 4l-8 8"/></svg>',
  plus: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M8 3v10M3 8h10"/></svg>',
  cal: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><rect x="2.5" y="3.5" width="11" height="10" rx="1.5"/><path d="M2.5 6.5h11M5.5 2v3M10.5 2v3"/></svg>',
  chev: '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6.5l4 4 4-4"/></svg>',
};
